import clsx from "clsx";
import {
  BarChart3,
  CalendarCheck,
  CalendarDays,
  Car,
  CreditCard,
  IdCard,
  LayoutDashboard,
  LogOut,
  Menu,
  Palette,
  ShieldAlert,
  SlidersHorizontal,
  Users,
  UserSquare2,
  Wrench,
  X,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";

import { clearSession } from "@/lib/api";
import { LocaleProvider, idiomaDoPainel, traduzir, type Chave } from "@/lib/i18n";
import { useMe } from "@/lib/queries";
import type { Me } from "@/lib/types";
import SubscriptionBanner from "./SubscriptionBanner";
import { Button, Card, Spinner } from "./ui";

/** Navegação por papel.
 *
 * O profissional entra para ver a agenda DELE — é a primeira e quase única
 * tela que ele usa, então vira a home. O dono entra para ver o resumo da
 * casa. Mostrar o mesmo menu para os dois faz o profissional se perder.
 */
function navFor(me: Me, t: (chave: Chave) => string) {
  const eProfissional = me.role === "professional";

  const itens = [
    eProfissional
      ? { to: "/", label: t("nav.myAgenda"), icon: CalendarCheck, end: true }
      : { to: "/", label: t("nav.home"), icon: LayoutDashboard, end: true },
  ];

  // Profissional sem permissão de ver outras agendas não tem o que fazer na
  // visão geral — ela mostraria só o que ele já vê em "Minha agenda".
  if (!eProfissional || me.permissions.see_all_agendas) {
    itens.push({ to: "/agenda", label: t("nav.houseAgenda"), icon: CalendarDays, end: false });
  }

  itens.push(
    { to: "/clientes", label: t("nav.customers"), icon: Users, end: false },
    // Veículos ao lado de clientes, e não dentro: no balcão a busca começa
    // pela placa, não pelo nome de quem trouxe o carro.
    { to: "/veiculos", label: t("nav.vehicles"), icon: Car, end: false },
  );

  // Faturamento e comissão da casa inteira. `see_financials` e não
  // `is_admin` porque é essa a permissão que o backend cobra na rota
  // (`CanSeeFinancials`) — se um dia as duas deixarem de coincidir, o menu
  // acompanha sozinho em vez de levar a um 403.
  if (me.permissions.see_financials) {
    itens.push({ to: "/relatorios", label: t("nav.reports"), icon: BarChart3, end: false });
  }

  // Só quem tem perfil de atendimento: recepção e gerente sem agenda não
  // teriam o que editar lá, e o item levaria a uma tela vazia.
  if (me.professional_id) {
    itens.push({ to: "/meu-perfil", label: t("nav.profile"), icon: IdCard, end: false });
  }

  if (me.permissions.is_admin) {
    itens.push(
      { to: "/servicos", label: t("nav.services"), icon: Wrench, end: false },
      { to: "/equipe", label: t("nav.team"), icon: UserSquare2, end: false },
      { to: "/meu-site", label: t("nav.mySite"), icon: Palette, end: false },
      { to: "/configuracoes", label: t("nav.scheduleRules"), icon: SlidersHorizontal, end: false },
    );
  }

  // Só o dono: é ele quem o backend deixa mexer em plano e cobrança
  // (`IsOwner`), e um item de menu que leva a 403 é pior que item nenhum.
  if (me.permissions.manage_billing) {
    itens.push({ to: "/assinatura", label: t("nav.subscription"), icon: CreditCard, end: false });
  }
  return itens;
}

const ROLE_KEY: Record<string, Chave> = {
  owner: "role.owner",
  manager: "role.manager",
  receptionist: "role.receptionist",
  professional: "role.professional",
};

/** O idioma do painel: preferência da pessoa, depois a da oficina.
 *
 * Mesma ordem do backend (apps/common/locale.py). Enquanto o `/me/` não
 * chegou — e nas telas de erro, quando ele nunca chega — vale o do
 * navegador: é melhor errar para a língua provável do que servir a tela
 * de "conta suspensa" num idioma que a pessoa não lê. */
export default function Layout() {
  const { data: me, isLoading, isError, error } = useMe();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const idioma = idiomaDoPainel(me);
  // Fora do Provider ainda (ele envolve só a árvore final), então a
  // tradução destas telas é chamada direto.
  const t = (chave: Chave) => traduzir(idioma, chave);

  const logout = useCallback(() => {
    clearSession(qc);
    navigate("/entrar", { replace: true });
  }, [qc, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  // Falha ao carregar o /me/. Antes isto limpava a sessão e navegava
  // DURANTE o render — efeito colateral em render, que o React não
  // garante executar, e que devolvia o usuário para o login sem dizer
  // nada. Quem entrava com a senha certa mas sem vínculo ativo (403)
  // via só a tela de login de volta e concluía que a senha estava errada.
  if (isError || !me) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    const semVinculo = status === 403;
    // 402 é a conta suspensa: nem leitura passa, então nem o /me/ volta.
    // Sem este caso o painel dizia "o servidor não respondeu como
    // esperado" — a pessoa procuraria defeito de conexão a tarde inteira
    // em vez de ver que a assinatura venceu.
    const suspensa = status === 402;

    const titulo = suspensa
      ? t("boot.suspendedTitle")
      : semVinculo
        ? t("boot.noGarageTitle")
        : t("boot.failedTitle");

    const texto = suspensa
      ? t("boot.suspendedText")
      : semVinculo
        ? t("boot.noGarageText")
        : t("boot.failedText");

    return (
      <div className="flex min-h-dvh items-center justify-center p-4">
        <Card className="w-full max-w-md p-6 text-center">
          <ShieldAlert className="mx-auto mb-3 text-racing-500" size={32} />
          <h1 className="font-display text-xl font-bold">{titulo}</h1>
          <p className="mt-2 text-sm text-steel-muted">{texto}</p>
          <div className="mt-5 flex justify-center gap-2">
            <Button variant="secondary" onClick={logout}>
              {t("nav.signOut")}
            </Button>
            {suspensa && (
              // A tela de assinatura vive fora deste Layout justamente
              // para abrir aqui: é o único caminho que o dono tem de sair
              // do bloqueio sozinho, sem esperar resposta de e-mail.
              <NavLink
                to="/assinatura"
                className="inline-flex items-center justify-center gap-2 rounded-[4px] bg-racing-500 px-4 py-2.5 text-xs font-semibold tracking-[0.12em] text-white uppercase transition hover:bg-racing-400"
              >
                {t("boot.reactivate")}
              </NavLink>
            )}
            {!semVinculo && !suspensa && (
              <Button onClick={() => qc.invalidateQueries({ queryKey: ["me"] })}>
                {t("boot.retry")}
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Senha provisória: nada do painel abre antes de ele escolher a dele.
  // A trava mora aqui, e não em cada rota, porque todas as telas do
  // painel passam por este Layout — inclusive as que forem criadas depois.
  if (me.must_change_password) {
    return <Navigate to="/nova-senha" replace />;
  }

  const itens = navFor(me, t);

  return (
    // O Provider envolve o painel inteiro: daqui para baixo `useT()` e
    // `useLocale()` funcionam sem ninguém passar o idioma de mão em mão.
    // A moeda vem da oficina — é a que ELA cobra dos clientes dela, e é
    // ela que aparece em serviço, agenda e relatório.
    <LocaleProvider idioma={idioma} moeda={me.garage.currency || me.garage.region.currency}>
    <div className="min-h-dvh lg:flex">
      {/* topo mobile */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-line bg-carbon-900/95 px-4 py-3 backdrop-blur lg:hidden">
        <div className="min-w-0">
          <p className="truncate font-display text-lg font-bold">{me.garage.name}</p>
          <p className="truncate text-xs text-steel-dim">
            {me.full_name.split(" ")[0]} · {t(ROLE_KEY[me.role])}
          </p>
        </div>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
          aria-expanded={menuOpen}
          className="rounded p-2 text-steel-muted transition hover:bg-carbon-700 hover:text-steel"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>

      <aside
        className={clsx(
          "border-line bg-carbon-800 lg:flex lg:w-60 lg:shrink-0 lg:flex-col lg:border-r",
          menuOpen ? "block border-b" : "hidden",
        )}
      >
        <div className="hidden border-b border-line px-5 py-5 lg:block">
          <p className="truncate font-display text-lg font-bold">{me.garage.name}</p>
          <p className="mt-0.5 text-xs text-steel-dim">
            {me.garage.plan?.name ?? t("nav.noPlan")}
            {me.garage.subscription_status === "trialing" && t("nav.onTrial")}
          </p>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {itens.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                clsx(
                  "flex items-center gap-3 rounded-[2px] px-3 py-3 text-sm font-medium transition",
                  // Item ativo com a barra acesa à esquerda, do jeito que
                  // um canal selecionado acende no painel do carro.
                  isActive
                    ? "border-l-2 border-nitro-500 bg-nitro-500/10 pl-[10px] text-nitro-400"
                    : "text-steel-muted hover:bg-carbon-700 hover:text-steel",
                )
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-line p-3">
          <div className="hidden px-3 pb-3 lg:block">
            <p className="truncate text-sm text-steel">{me.full_name}</p>
            <p className="text-xs text-steel-dim">{t(ROLE_KEY[me.role])}</p>
          </div>
          <button
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-[4px] px-3 py-3 text-sm font-medium text-steel-muted transition hover:bg-carbon-700 hover:text-steel"
          >
            <LogOut size={18} />
            {t("nav.signOut")}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <SubscriptionBanner subscription={me.subscription} />
        <Outlet context={me} />
      </main>
    </div>
    </LocaleProvider>
  );
}
